import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Droplet, Leaf, MapPin, Trees } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { translations } from "../../translations/translations";
import { preloadReservationRequests } from "../../utils/routePreload";
import mobileHeroFallback from "../../assets/fallback1.PNG";
import "./HomeHeroSection.css";

const ICONS = {
  leaf: Leaf,
  droplet: Droplet,
  leafWide: Trees,
  pin: MapPin,
};

const MOBILE_BREAKPOINT = "(max-width: 768px)";
const DESKTOP_VIDEO = "/output_slow.mp4";
const MOBILE_VIDEO = "/MobileHeroVideo.mp4";

function HomeHeroSection() {
  const { language } = useLanguage();
  const t = translations?.[language] || translations?.en || {};
  const hero = t.homeHero;

  const videoRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(MOBILE_BREAKPOINT).matches
      : false,
  );
  const [useMobileFallback, setUseMobileFallback] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_BREAKPOINT);
    const handleChange = (event) => {
      setIsMobile(event.matches);
      if (!event.matches) {
        setUseMobileFallback(false);
        setVideoReady(false);
      }
    };
    mq.addEventListener("change", handleChange);
    return () => mq.removeEventListener("change", handleChange);
  }, []);

  const videoSrc = isMobile ? MOBILE_VIDEO : DESKTOP_VIDEO;

  useEffect(() => {
    if (!isMobile) {
      setUseMobileFallback(false);
      setVideoReady(false);
      return;
    }

    setUseMobileFallback(false);
    setVideoReady(false);

    const video = videoRef.current;
    if (!video) return;

    let cancelled = false;

    const showFallback = () => {
      if (!cancelled) setUseMobileFallback(true);
    };

    const attemptPlay = async () => {
      if (cancelled || !video) return;
      try {
        await video.play();
        if (!cancelled && !video.paused) {
          setVideoReady(true);
        }
      } catch {
        showFallback();
      }
    };

    const handlePlaying = () => {
      if (!cancelled) setVideoReady(true);
    };

    const handleError = () => {
      showFallback();
    };

    video.addEventListener("playing", handlePlaying);
    video.addEventListener("error", handleError);

    if (video.readyState >= 2) {
      attemptPlay();
    } else {
      video.addEventListener("loadeddata", attemptPlay, { once: true });
    }

    const fallbackTimer = window.setTimeout(() => {
      if (!cancelled && video.paused && !video.ended) {
        showFallback();
      }
    }, 2000);

    return () => {
      cancelled = true;
      window.clearTimeout(fallbackTimer);
      video.removeEventListener("playing", handlePlaying);
      video.removeEventListener("error", handleError);
      video.removeEventListener("loadeddata", attemptPlay);
    };
  }, [isMobile, videoSrc]);

  const features = Array.isArray(hero?.features) ? hero.features : [];

  const showMobileFallback = isMobile && useMobileFallback;

  return (
    <div
      className={[
        "home-hero",
        showMobileFallback ? "home-hero--fallback" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {showMobileFallback ? (
        <img
          className="home-hero__fallback"
          src={mobileHeroFallback}
          alt=""
          aria-hidden="true"
        />
      ) : (
        <video
          ref={videoRef}
          key={videoSrc}
          className={[
            "home-hero__video",
            isMobile && !videoReady ? "home-hero__video--pending" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          onLoadedData={(event) => {
            event.currentTarget.playbackRate = 0.85;
          }}
        >
          <source src={videoSrc} type="video/mp4" />
        </video>
      )}
      <div className="home-hero__overlay" aria-hidden="true" />
      <div className="home-hero__inner">
        <div className="home-hero__copy">
          <h1 className="home-hero__title">{hero?.title}</h1>
          <p className="home-hero__tagline">{hero?.tagline}</p>
          <Link
            to="/reservations"
            className="home-hero__cta"
            onMouseEnter={preloadReservationRequests}
            onFocus={preloadReservationRequests}
          >
            <span>{t.bookNow}</span>
            <span className="home-hero__cta-arrow" aria-hidden="true">
              →
            </span>
          </Link>
        </div>

        <div className="home-hero__banner" role="list">
          {features.map((item, index) => {
            const Icon = ICONS[item.icon] || Leaf;
            return (
              <div className="home-hero__banner-cell" role="listitem" key={index}>
                <div className="home-hero__banner-icon">
                  <Icon
                    className="home-hero__banner-svg"
                    size={26}
                    strokeWidth={1.35}
                    aria-hidden
                  />
                </div>
                <p className="home-hero__banner-title">{item.title}</p>
                <p className="home-hero__banner-desc">{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default HomeHeroSection;
